import { sortReservationItems } from '../../utils/reservationUtils';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import validator from 'validator';
import { toast } from 'react-toastify';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createReservation, putReservation } from '../../redux/slices/reservationsSlice';
import seatInfoFormatter from '../../utils/formatters';
import { useTheme, useMediaQuery } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { normalizeOptionalPhone } from '../../utils/phoneUtils';

const formConfig = () => ({
  firstName: {
    value: '',
    error: false,
    errorText: '',
    isValid: (value) => {
      return true;
    },
  },

  lastName: {
    value: '',
    error: false,
    errorText: '',
    isValid: (value) => {
      return true;
    },
  },

  email: {
    value: '',
    error: false,
    errorText: 'Invalid email',
    isValid: (value) => {
      return validator.isEmpty(value, { ignore_whitespace: true }) || validator.isEmail(value);
    },
  },

  phone: {
    value: '',
    error: false,
    errorText: 'Invalid phone',
    isValid: (value) => {
      return normalizeOptionalPhone(value).isValid;
    },
  },
  notes: {
    value: '',
    error: false,
    errorText: '',
    isValid: (value) => {
      return true;
    },
  },
});

export default function DetailsDialog({
  isEventScope,
  reservation,
  session,
  event,
  seats,
  open,
  onClose,
  onSaveSuccess,
}) {
  const { t } = useTranslation();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const [formState, setFormState] = useState(formConfig());
  const [expiration, setExpiration] = useState(null);
  const [expires, setExpires] = useState(false);

  const isLoading = useSelector((state) => state.reservations.isLoading);

  const actionButtonDisabled = useMemo(() => {
    if (reservation) {
      const { seats: oldSeats } = reservation.cart;

      const oldState = {
        firstName: reservation.first_name,
        lastName: reservation.last_name,
        email: reservation.email,
        phone: reservation.phone,
        notes: reservation.notes,
        expiration: reservation.expiration,
        seats: sortReservationItems(oldSeats),
      };

      const newState = {
        firstName: formState.firstName.value || null,
        lastName: formState.lastName.value || null,
        email: formState.email.value || null,
        phone: formState.phone.value || null,
        notes: formState.notes.value || null,
        expiration: expiration && expiration.toISOString(),
        seats: sortReservationItems(seats),
      };

      const changed = JSON.stringify(oldState) !== JSON.stringify(newState);

      if (isLoading || !seats.length || !changed) return true;
    } else {
      if (isLoading || !seats.length) {
        return true;
      }
    }
    return false;
  }, [isLoading, seats, reservation, formState, expiration]);

  const actionButtonText = useMemo(() => (!reservation ? t('Create') : t('Update')), [reservation, t]);

  useEffect(() => {
    if (open && reservation) {
      const { first_name, last_name, email, phone, notes, expiration } = reservation;

      if (expiration) {
        setExpiration(dayjs(expiration));
        setExpires(true);
      }

      setFormState((currentState) => ({
        ...currentState,
        firstName: {
          ...currentState.firstName,
          ...(first_name !== null ? { value: first_name } : {}),
        },
        lastName: {
          ...currentState.lastName,
          ...(last_name !== null ? { value: last_name } : {}),
        },
        email: {
          ...currentState.email,
          ...(email !== null ? { value: email } : {}),
        },
        phone: {
          ...currentState.phone,
          ...(phone !== null ? { value: phone } : {}),
        },
        notes: {
          ...currentState.notes,
          ...(notes !== null ? { value: notes } : {}),
        },
      }));
    }
  }, [open, reservation]);

  useEffect(() => {
    if (!open) {
      setFormState(formConfig());
      setExpiration(null);
      setExpires(false);
    }
  }, [open]);

  const objectIds = useMemo(
    () =>
      seats.map(({ id, typeId, typeLabel, isDefaultType }) => ({
        id,
        typeId,
        typeLabel,
        isDefaultType,
      })),
    [seats]
  );

  const onCloseInternal = () => {
    onClose();
  };

  const onInputChange = (input) => {
    setFormState((currentState) => ({
      ...currentState,
      [input.target.name]: {
        ...currentState[input.target.name],
        value: input.target.type === 'checkbox' ? input.target.checked : input.target.value,
        error: false,
      },
    }));
  };

  const dispatchCreateReservation = useCallback(
    (payload) => {
      dispatch(createReservation(payload))
        .unwrap()
        .then(() => {
          onSaveSuccess();
          toast.success(t('Reservation created successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
        })
        .catch((err) => {
          if (err.data?.errorType === 'RESERVATION_INVENTORY_UNCERTAIN') {
            onSaveSuccess();
            toast.error(t('Reservation needs review. Please contact support before trying again.'), {
              position: toast.POSITION.BOTTOM_RIGHT,
              autoClose: false,
            });
          } else if (err.data?.errorType === 'INVALID_EMAIL_DOMAIN_ERROR') {
            const newState = Object.assign({}, formState);
            newState['email'].error = true;
            setFormState(newState);
          } else {
            toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
          }
        });
    },
    [formState, setFormState, onSaveSuccess, dispatch, t]
  );

  const dispatchUpdateReservation = useCallback(
    (payload) => {
      dispatch(putReservation(payload))
        .unwrap()
        .then(() => {
          onSaveSuccess();
          toast.success(t('Reservation updated successfuly'), { position: toast.POSITION.BOTTOM_RIGHT });
        })
        .catch((err) => {
          if (err.data?.errorType === 'RESERVATION_INVENTORY_UNCERTAIN') {
            onSaveSuccess();
            toast.error(t('Reservation needs review. Please contact support before trying again.'), {
              position: toast.POSITION.BOTTOM_RIGHT,
              autoClose: false,
            });
          } else if (err.data?.errorType === 'INVALID_EMAIL_DOMAIN_ERROR') {
            const newState = Object.assign({}, formState);
            newState['email'].error = true;
            setFormState(newState);
          } else {
            toast.error(t('An error occured please try again!'), { position: toast.POSITION.BOTTOM_RIGHT });
          }
        });
    },
    [formState, setFormState, onSaveSuccess, dispatch, t]
  );

  const onAction = useCallback(() => {
    const inputs = Object.keys(formState);
    const newState = Object.assign({}, formState);
    let hasError = false;
    for (const input of inputs) {
      if (!newState[input].isValid(newState[input].value)) {
        hasError = true;
        newState[input].error = true;
      }
    }

    setFormState(newState);

    if (!hasError) {
      const payload = {
        session: session.current,
        ...(isEventScope ? { event_id: event.id } : {}),
        user: {
          email: newState.email.value,
          first_name: newState.firstName.value,
          last_name: newState.lastName.value,
          phone: normalizeOptionalPhone(newState.phone.value).value,
        },
        expiration: expiration && expiration.toISOString(),
        notes: newState.notes.value,
        object_ids: objectIds,
      };

      if (reservation) {
        payload.reservation_id = reservation.id;
        dispatchUpdateReservation(payload);
      } else {
        dispatchCreateReservation(payload);
      }
    }
  }, [
    isEventScope,
    reservation,
    dispatchUpdateReservation,
    dispatchCreateReservation,
    formState,
    expiration,
    event.id,
    objectIds,
    session,
  ]);

  return (
    <Dialog fullWidth maxWidth="sm" fullScreen={fullScreen} open={open}>
      <DialogTitle gutterBottom>
        <Box display="flex" alignItems="center">
          <ScheduleIcon sx={{ marginRight: 1 }} />
          {t('Reservation Details')}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onCloseInternal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 16,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid container columnSpacing={2}>
          <Grid item xs={6}>
            <TextField
              name="firstName"
              value={formState.firstName.value}
              onChange={onInputChange}
              error={formState.firstName.error}
              helperText={formState.firstName.error ? formState.firstName.errorText : ''}
              fullWidth
              variant="standard"
              type="text"
              label={t('First name')}
              margin="dense"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              name="lastName"
              value={formState.lastName.value}
              onChange={onInputChange}
              error={formState.lastName.error}
              helperText={formState.lastName.error ? formState.lastName.errorText : ''}
              fullWidth
              variant="standard"
              type="text"
              label={t('Last name')}
              margin="dense"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              name="email"
              value={formState.email.value}
              onChange={onInputChange}
              error={formState.email.error}
              helperText={formState.email.error ? formState.email.errorText : ''}
              fullWidth
              variant="standard"
              type="email"
              label={t('Email')}
              margin="dense"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              value={formState.phone.value}
              onChange={onInputChange}
              error={formState.phone.error}
              helperText={formState.phone.error ? formState.phone.errorText : ''}
              name="phone"
              fullWidth
              variant="standard"
              type="tel"
              label={t('Phone')}
              margin="dense"
            />
          </Grid>
          <Grid item xs={9}>
            <DateTimePicker
              disablePast
              maxDateTime={dayjs(event.date)}
              disabled={!expires}
              value={expiration}
              onChange={(value) => setExpiration(value)}
              // TODO: investigate why when setting expiration to +1 minute from current time gives error
              //onError={(err) => console.log(err)}
              name="expiration"
              slotProps={{
                textField: {
                  margin: 'dense',
                  label: t('Expiration date'),
                  fullWidth: true,
                  variant: 'standard',
                },
              }}
            />
          </Grid>
          <Grid item xs={3} container justifyContent="center">
            <FormGroup sx={{ mt: 3 }}>
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Switch
                    checked={expires}
                    onChange={() => {
                      setExpiration(null);
                      setExpires((exp) => !exp);
                    }}
                  />
                }
                label={t('Expires')}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <TextField
              multiline
              maxRows={10}
              value={formState.notes.value}
              onChange={onInputChange}
              error={formState.notes.error}
              helperText={formState.notes.error ? formState.notes.errorText : ''}
              fullWidth
              variant="standard"
              margin="dense"
              name="notes"
              label={t('Notes')}
            />
          </Grid>
        </Grid>
        <Typography sx={{ mt: 2 }} gutterBottom variant="h6">
          {t('Event Details')}
        </Typography>
        <Typography variant="subtitle1">
          {event.title}
          {t('-')}
          {dayjs(event.date).format('dddd DD MMMM YYYY HH:mm')}
        </Typography>
        <Typography gutterBottom variant="subtitle1">
          {event.location}
        </Typography>
        <Typography gutterBottom variant="subtitle1">
          {event.city}
        </Typography>
        <Divider />
        {seats.length ? (
          <List sx={{ maxHeight: 200, overflow: 'auto' }}>
            {seats.map(({ id, typeLabel, isDefaultType, section, row, seat, categoryLabel }, index) => (
              <ListItem disablePadding key={`${id}-${index}`}>
                <ListItemText
                  primary={
                    <>
                      {categoryLabel}
                      {!isDefaultType && <Chip sx={{ marginLeft: 1 }} size="small" label={typeLabel} />}
                    </>
                  }
                  secondary={seatInfoFormatter(section, row, seat, t)}
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ height: '100px' }} display="flex" alignItems="center" justifyContent="center">
            <Typography variant="subtitle1">{t('No seat selected')}</Typography>
          </Box>
        )}
        <Divider />
      </DialogContent>
      <DialogActions sx={{ paddingTop: 0, paddingLeft: 2.5, paddingRight: 2.5, paddingBottom: 2.5 }}>
        <Button sx={{ minWidth: 150 }} disabled={actionButtonDisabled} onClick={onAction} variant="contained">
          {actionButtonText}
          {isLoading && <CircularProgress size={20} sx={{ ml: 1, color: 'white' }} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
